import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { BRAND } from '@tarot/shared';
import { StarMark } from '../brand/BrandLogo';
import { clientConfig } from '../lib/config';
import { isCrawler, isMobileDevice } from '../lib/device';
import { webGateOn } from '../lib/platform';
import { tossEntryUrl } from '../lib/toss';
import { PrimaryAnchor } from './Buttons';

/**
 * **웹은 토스 전용** (2026-10-10~). 타로는 토스 미니앱에서만 본다.
 *
 *  - 서비스 화면(홈·나 혼자·방·공유 결과·로그인·지난 타로·내 정보) — `TossOnlyRoutes`
 *    · 모바일: 미니앱으로 바로 보낸다. 진입 링크(토스 공유 링크)는 목적지가 미니앱 홈으로
 *      고정이라 방 초대·공유 결과도 홈에 내린다. 진입 링크가 없을 때만 `intoss://`로 같은 경로를 연다
 *      (lib/toss.ts `tossEntryUrl`)
 *    · PC: "모바일 토스에서 이용해 주세요" 안내만 띄우고 화면은 열지 않는다
 *  - 읽을거리(카드 도감·용어 사전·안내·약관·개인정보·문의) — `TossAppBannerRoutes`
 *    · 그대로 열고 위에 토스 안내만 얹는다. 약관·처리방침은 소셜 로그인·토스 콘솔에 주소가
 *      등록돼 있어 누구나 열 수 있어야 하고, 카드 도감은 검색 유입의 주력이다 (doc/seo.md)
 *
 * 켜고 끄는 값은 `webGateOn`(lib/platform.ts). 미니앱 번들에서는 항상 꺼져 있다.
 */

/** 서비스 화면 묶음 — 웹에서는 안내로 갈음한다 */
export function TossOnlyRoutes() {
  return webGateOn ? <TossOnlyNotice /> : <Outlet />;
}

/** 읽을거리 묶음 — 웹에서는 위에 토스 안내를 얹는다 */
export function TossAppBannerRoutes() {
  return (
    <>
      {webGateOn && <TossAppBanner />}
      <Outlet />
    </>
  );
}

/**
 * 자동 이동은 **페이지를 연 뒤 한 번만** 한다.
 * 토스가 없는 기기에서 `intoss://`는 열리지 않는다. 그 상태로 다른 화면을 오갈 때마다
 * 다시 시도하면 "열 수 없는 주소" 경고가 계속 뜬다 — 그 뒤로는 버튼을 누르게 둔다.
 */
let autoOpened = false;

/** 지금 화면(또는 `target`)에 해당하는 미니앱 진입 주소. 설정을 받기 전에는 딥링크로 시작한다 */
function useTossEntryUrl(target: string): string {
  const [href, setHref] = useState(() => tossEntryUrl(target, ''));
  useEffect(() => {
    let alive = true;
    void clientConfig().then((c) => alive && setHref(tossEntryUrl(target, c.tossEntryLink)));
    return () => {
      alive = false;
    };
  }, [target]);
  return href;
}

function TossOnlyNotice() {
  const mobile = isMobileDevice();
  const target = location.href; // 진입 링크가 없을 때(intoss://)만 쓰인다 — 같은 화면으로 연다
  const href = useTossEntryUrl(target);

  useEffect(() => {
    // 봇은 보내지 않는다 — 화면은 사람과 같게 보여 주되(클로킹 금지) 앱 링크로 튕기지 않는다
    if (!mobile || autoOpened || isCrawler()) return;
    autoOpened = true;
    void clientConfig().then((c) => {
      // replace — 뒤로 가기로 이 화면에 돌아와 다시 튕기는 고리를 만들지 않는다
      location.replace(tossEntryUrl(target, c.tossEntryLink));
    });
  }, [mobile, target]);

  return (
    <section className="flex flex-1 flex-col items-center justify-center gap-4 py-12 text-center">
      <StarMark className="h-12 w-12 text-gold" />
      {mobile ? (
        <>
          <h2 className="text-xl font-bold">토스 앱으로 이동하고 있어요</h2>
          <p className="text-sm leading-relaxed text-moon-dim">
            {BRAND.nameKo}는 토스 앱에서 이용할 수 있어요.
            <br />
            열리지 않으면 아래 버튼을 눌러 주세요.
          </p>
          <div className="flex w-full max-w-xs flex-col gap-2">
            <PrimaryAnchor href={href}>토스에서 열기</PrimaryAnchor>
          </div>
          <p className="text-xs leading-relaxed text-moon-dim/70">
            토스 앱이 없다면 설치한 뒤 &lsquo;{BRAND.nameKo}&rsquo;를 검색해 주세요.
          </p>
        </>
      ) : (
        <>
          <h2 className="text-xl font-bold">모바일 토스에서 이용해 주세요</h2>
          <p className="text-sm leading-relaxed text-moon-dim">
            {BRAND.nameKo}는 <b className="text-moon">휴대폰의 토스 앱</b>에서 이용할 수 있어요.
            <br />
            토스 앱을 열고 &lsquo;{BRAND.nameKo}&rsquo;를 검색해 주세요.
          </p>
        </>
      )}
      <Link to="/cards" className="text-sm text-gold underline underline-offset-2">
        카드 도감 둘러보기
      </Link>
    </section>
  );
}

function TossAppBanner() {
  const mobile = isMobileDevice();
  const href = useTossEntryUrl(`${location.origin}/`);
  return (
    <aside className="mb-5 flex items-center gap-3 rounded-2xl border border-gold/25 bg-gold/5 px-4 py-3">
      <StarMark className="h-6 w-6 shrink-0 text-gold" />
      <p className="flex-1 text-xs leading-relaxed text-moon-dim">
        {mobile ? (
          <>
            타로 보기는 <b className="text-moon">토스 앱</b>에서 할 수 있어요.
          </>
        ) : (
          <>
            타로 보기는 <b className="text-moon">모바일 토스 앱</b>에서 할 수 있어요. 토스에서
            &lsquo;{BRAND.nameKo}&rsquo;를 검색해 주세요.
          </>
        )}
      </p>
      {mobile && (
        <a
          href={href}
          className="shrink-0 text-xs font-semibold text-gold underline underline-offset-2"
        >
          토스에서 열기
        </a>
      )}
    </aside>
  );
}
